import {
  type AnyRouteDescriptor,
  buildPath,
  ErrorBody,
  type ErrorCode,
  PlatformError,
  type RouteResponse,
} from '@agentic/contracts';

/** Transport settings shared by every call. */
export interface TransportOptions {
  /** Base URL, absolute (`http://localhost:4000`) or relative to the page (`/api`). */
  baseUrl: string;
  /** Custom fetch (tests, Bun servers). Defaults to `globalThis.fetch`. */
  fetch?: typeof fetch;
  /** Extra headers on every request (e.g. `authorization` for Bun scripts). */
  headers?: Record<string, string> | (() => Record<string, string>);
  /** Defaults to `include` so the `agentic_session` cookie travels with requests. */
  credentials?: RequestCredentials;
  /** Parse success bodies with the descriptor's response schema (applies defaults). Default true. */
  validateResponses?: boolean;
  /** Called for every failed call before the error is thrown (toasts, 401 redirects). */
  onError?: (error: PlatformError, route: AnyRouteDescriptor) => void;
}

/** Untyped request parts accepted by `callRoute`. */
export interface RawCallInput {
  params?: Record<string, string | number> | undefined;
  query?: Record<string, unknown> | undefined;
  body?: unknown;
  headers?: Record<string, string> | undefined;
  signal?: AbortSignal | undefined;
}

const STATUS_CODES: Record<number, ErrorCode> = {
  400: 'validation_failed',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  423: 'kill_switch',
  429: 'limit_exceeded',
  502: 'downstream_failed',
  503: 'downstream_failed',
  504: 'timeout',
};

/** Serialises a query object; arrays repeat the key, `undefined`/`null` are skipped. */
export function toQueryString(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) search.append(key, typeof v === 'object' ? JSON.stringify(v) : String(v));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** Joins the base URL and a descriptor path with params and query applied. */
export function routeUrl(baseUrl: string, descriptor: AnyRouteDescriptor, input: RawCallInput = {}): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${base}${buildPath(descriptor.path, input.params ?? {})}${toQueryString(input.query)}`;
}

export function resolveHeaders(options: TransportOptions): Record<string, string> {
  const h = options.headers;
  return typeof h === 'function' ? h() : { ...(h ?? {}) };
}

/** Builds a PlatformError from a non-2xx response (ErrorBody when present, status mapping otherwise). */
export async function errorFromResponse(res: Response, routeId: string): Promise<PlatformError> {
  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = undefined;
  }
  const parsed = ErrorBody.safeParse(payload);
  if (parsed.success) {
    const { error } = parsed.data;
    return new PlatformError(error.code, error.message, {
      retryable: error.retryable,
      details: {
        ...(error.details ?? {}),
        httpStatus: res.status,
        route: routeId,
        ...(error.traceId === undefined ? {} : { traceId: error.traceId }),
      },
    });
  }
  const code = STATUS_CODES[res.status] ?? (res.status >= 500 ? 'internal' : 'validation_failed');
  return new PlatformError(code, `${routeId} failed with HTTP ${res.status}`, {
    details: { httpStatus: res.status, route: routeId },
  });
}

/**
 * Calls one route. Throws `PlatformError` for non-2xx responses, network failures and (when
 * `validateResponses`) bodies that do not match the contract. `AbortError`s are rethrown unchanged.
 */
export async function callRoute<D extends AnyRouteDescriptor>(
  descriptor: D,
  input: RawCallInput,
  options: TransportOptions,
): Promise<RouteResponse<D>> {
  if (descriptor.responseType === 'sse') {
    throw new PlatformError('validation_failed', `${descriptor.id} is an event stream; use openEventStream`);
  }
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const headers: Record<string, string> = {
    accept: descriptor.responseType === 'text' ? 'text/plain' : 'application/json',
    ...resolveHeaders(options),
    ...(input.headers ?? {}),
  };
  let body: string | undefined;
  if (input.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(input.body);
  }
  const fail = (error: PlatformError): never => {
    options.onError?.(error, descriptor);
    throw error;
  };

  let res: Response;
  try {
    res = await doFetch(routeUrl(options.baseUrl, descriptor, input), {
      method: descriptor.method.toUpperCase(),
      headers,
      ...(body === undefined ? {} : { body }),
      credentials: options.credentials ?? 'include',
      ...(input.signal ? { signal: input.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof Error && cause.name === 'AbortError') throw cause;
    return fail(
      new PlatformError('downstream_failed', `Network error calling ${descriptor.id}`, {
        cause,
        details: { route: descriptor.id },
      }),
    );
  }

  if (!res.ok) return fail(await errorFromResponse(res, descriptor.id));
  if (res.status === 204 || descriptor.successStatus === 204) return undefined as RouteResponse<D>;
  if (descriptor.responseType === 'text') return (await res.text()) as RouteResponse<D>;

  let json: unknown;
  try {
    json = await res.json();
  } catch (cause) {
    return fail(new PlatformError('internal', `${descriptor.id} returned invalid JSON`, { cause }));
  }
  if (options.validateResponses === false) return json as RouteResponse<D>;
  const parsed = descriptor.response.safeParse(json);
  if (!parsed.success) {
    return fail(
      new PlatformError('internal', `${descriptor.id} response does not match the contract`, {
        details: {
          route: descriptor.id,
          issues: parsed.error.issues
            .slice(0, 10)
            .map((i) => ({ path: i.path.join('.'), message: i.message })),
        },
      }),
    );
  }
  return parsed.data as RouteResponse<D>;
}
